/**
 * ReleaseSelectedResultView — the Zwolnij post-commit result for a
 * `mode='selected'` release (CU-86cbh03pj, partial-reservation-release task 4.1).
 *
 * Mirrors `unified-select-popover.tsx`'s `renderReserveResult` (post-commit
 * outcome display) and `unified-select-release-breakdown-view.tsx` (pre-commit
 * breakdown rendering of the same released/remaining-range vocabulary), but
 * renders the ACTUAL per-hold outcome the release call returned — authoritative
 * over the breakdown's prediction whenever they disagree (spec: "the result
 * view SHALL show the ranges the call actually returned, and SHALL NOT show
 * the breakdown's predicted ranges as though they had occurred").
 *
 * `BatchReleaseOutcomeItem`/`BatchReleaseSkippedItem` carry only a
 * `reservationId`, not a `mediaUnitId` — `reservationUnitLookup` (built by the
 * caller from the pre-commit `releasableCells`, the only place that mapping is
 * known) resolves each entry back to the nośnik it names.
 */
import { useTranslation } from 'react-i18next';
import { Button } from '@ooh/ui/components/button';
import type {
  BatchReleaseOutcomeItem,
  BatchReleaseSelectedResponse,
  BatchReleaseSkippedReason,
} from '@ooh/contracts';
import {
  OutcomeResultEntryRow,
  RESULT_PANEL_WRAPPER_CLASS,
  type LooseT,
  type OutcomeResultUnitLookup,
} from './outcome-result-buckets';
import { formatDisplayDateRange } from '@ooh/ui/lib/date-format';

/** Names the remaining range(s) a shortened/split outcome actually left held —
 * `undefined` for a plain `released` outcome, which carries neither field. */
function releaseResultRemainingDetail(
  entry: BatchReleaseOutcomeItem,
  t: LooseT,
): string | undefined {
  if (entry.outcome === 'shortened') {
    return t('availability.select.releaseResultSelected.remainingRangeDetail', {
      range: formatDisplayDateRange(entry.remainingRange.from, entry.remainingRange.to),
    });
  }
  if (entry.outcome === 'split') {
    return t('availability.select.releaseResultSelected.remainingRangesDetail', {
      rangeA: formatDisplayDateRange(entry.remainingRanges[0].from, entry.remainingRanges[0].to),
      rangeB: formatDisplayDateRange(entry.remainingRanges[1].from, entry.remainingRanges[1].to),
    });
  }
  return undefined;
}

/**
 * Business-readable reason for a hold the release could not process — a fixed
 * translated message per reason, never the raw `BatchReleaseSkippedReason`
 * identifier, same convention as `releaseBreakdownUnresolvableDetail`/
 * `outcomeFailedDetail`. `NOT_IN_SELECTED_RANGE` gets its own distinct wording
 * (spec: "explained distinctly from sold, already-released, or does not exist").
 */
function releaseResultSkippedDetail(reason: BatchReleaseSkippedReason, t: LooseT): string {
  switch (reason) {
    case 'SOLD_NOT_RELEASABLE':
      return t('availability.select.releaseResultSelected.reasonSold');
    case 'NOT_HELD':
      return t('availability.select.releaseResultSelected.reasonNotHeld');
    case 'NOT_FOUND':
      return t('availability.select.releaseResultSelected.reasonNotFound');
    case 'NOT_IN_SELECTED_RANGE':
      return t('availability.select.releaseResultSelected.reasonNotInSelectedRange');
    default:
      return t('availability.select.releaseResultSelected.reasonGeneric');
  }
}

export interface ReleaseSelectedResultViewProps {
  response: BatchReleaseSelectedResponse;
  /** reservationId -> mediaUnitId, resolved from the pre-commit `releasableCells`. */
  reservationUnitLookup: Map<number, number>;
  unitLookup: OutcomeResultUnitLookup;
  onDismiss: () => void;
}

export function ReleaseSelectedResultView({
  response,
  reservationUnitLookup,
  unitLookup,
  onDismiss,
}: ReleaseSelectedResultViewProps) {
  const { t } = useTranslation('inventory');
  const looseT = t as LooseT;
  const unknownUnitLabel = t('availability.select.confirmReleaseUnknownUnit');

  function unitFor(reservationId: number) {
    const mediaUnitId = reservationUnitLookup.get(reservationId);
    return mediaUnitId === undefined ? undefined : unitLookup.get(mediaUnitId);
  }

  return (
    <div className={RESULT_PANEL_WRAPPER_CLASS} data-testid="unified-select-release-result">
      {response.outcomes.length > 0 ? (
        <ul className="space-y-0.5 text-xs text-muted-foreground">
          {response.outcomes.map((entry) => (
            <li
              key={`outcome-${entry.reservationId}`}
              data-testid={`unified-select-release-result-outcome-${entry.reservationId}`}
            >
              <OutcomeResultEntryRow
                unit={unitFor(entry.reservationId)}
                dateFrom={entry.outcome !== 'released' ? entry.releasedRange.from : undefined}
                dateTo={entry.outcome !== 'released' ? entry.releasedRange.to : undefined}
                detail={releaseResultRemainingDetail(entry, looseT)}
                unknownUnitLabel={unknownUnitLabel}
              />
            </li>
          ))}
        </ul>
      ) : null}
      {response.skipped.length > 0 ? (
        <ul className="space-y-0.5 text-xs text-destructive">
          {response.skipped.map((entry) => (
            <li
              key={`skipped-${entry.reservationId}`}
              data-testid={`unified-select-release-result-skipped-${entry.reservationId}`}
            >
              <OutcomeResultEntryRow
                unit={unitFor(entry.reservationId)}
                detail={releaseResultSkippedDetail(entry.reason, looseT)}
                unknownUnitLabel={unknownUnitLabel}
              />
            </li>
          ))}
        </ul>
      ) : null}
      <p
        className="text-xs text-muted-foreground"
        data-testid="unified-select-release-result-queues-left"
      >
        {t('availability.select.releaseResultSelected.queuesLeftLabel', {
          count: response.queuesLeft,
        })}
      </p>
      <Button
        type="button"
        variant="link"
        size="xs"
        className="h-auto p-0 text-xs"
        onClick={onDismiss}
        data-testid="unified-select-release-result-dismiss"
      >
        {t('availability.select.reserveResult.dismissBtn')}
      </Button>
    </div>
  );
}
